import { SalvLabel, SalvButton, Size } from '@salvapp/ui-components';
import { testRunStatusColorVariant } from '../../../common/versioning/helper';
import { formatEnumLabel } from '../../../common/helper';
import {
  InternalApiScenario,
  InternalApiScenarioContentTestResultInformation,
  InternalApiScenarioTestResultStatus,
} from '../../../api-internal';
import InternalLink from '../../../common/InternalLink';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import React, { useState } from 'react';
import ScenarioVersionModal from '../version-modal/ScenarioVersionModal';
import { scenarioTestingApi } from '../../../apis';
import { showFeedback } from '../../../feedback/feedbackReducer';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import ScenarioVersionTestModal from './ScenarioVersionTestModal';
import { useNavigate } from 'react-router';
import { testResultDashboardLink } from '../../../dashboard/dashboardHelpers';

export const TestRunStatusSection = ({
  testResult,
  scenario,
}: {
  testResult: InternalApiScenarioContentTestResultInformation;
  scenario: InternalApiScenario;
}) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showTestModal, setShowTestModal] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const isInProgress =
    testResult?.status === InternalApiScenarioTestResultStatus.SCHEDULED ||
    testResult?.status === InternalApiScenarioTestResultStatus.RUNNING;

  const cancelTestRun = async () => {
    setCancelling(true);
    try {
      await scenarioTestingApi.cancelScenarioContentTestRun({
        testHandle: testResult.testExecutionHandle!,
      });

      await queryClient.invalidateQueries({
        queryKey: ['testRunInformation'],
      });
    } catch (_) {
      dispatch(
        showFeedback({
          message: 'Oops, something went wrong!',
          success: false,
        })
      );
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div>
      <div className='scenario-status-section'>
        <div className='d-flex justify-content-between align-items-center'>
          <div>
            <div className='d-flex'>
              <div className='text-strong-m m-r-8 m-b-4'>Status</div>
              <SalvLabel variant={testRunStatusColorVariant(testResult.status)} className='m-r-8'>
                {formatEnumLabel(testResult.status)}
              </SalvLabel>
            </div>

            <div>
              {testResult.status === InternalApiScenarioTestResultStatus.FINISHED && (
                <InternalLink
                  title={
                    <>
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} className='fe me-2' />
                      Go to dashboard
                    </>
                  }
                  link={testResultDashboardLink(testResult.contentId, testResult.testExecutionId)}
                />
              )}

              {isInProgress && (
                <SalvButton
                  onClick={cancelTestRun}
                  label='Stop run'
                  type='submit'
                  size={Size.SMALL}
                  variant='danger'
                  disabled={cancelling}
                />
              )}
            </div>
          </div>

          <div>
            <SalvButton
              variant='muted'
              size={Size.SMALL}
              onClick={() => {
                setShowTestModal(true);
              }}
              label='Rerun test'
            />

            <SalvButton
              variant='muted'
              size={Size.SMALL}
              onClick={() => {
                setShowVersionModal(true);
              }}
              label='View original version'
              className='m-l-8'
            />
          </div>
        </div>

        {scenario && testResult && (
          <>
            <ScenarioVersionModal
              showModal={showVersionModal}
              setShowModal={setShowVersionModal}
              versionId={testResult.versionInfo.id}
              scenario={scenario}
              disabled={true}
            />

            <ScenarioVersionTestModal
              scenario={scenario}
              show={showTestModal}
              versionId={testResult.versionInfo.id}
              onComplete={() => {
                setShowTestModal(false);
                navigate(`/scenarios/${scenario.id}?tab=test-runs`);
              }}
              onClose={() => setShowTestModal(false)}
              testRunToDuplicate={testResult}
            />
          </>
        )}
      </div>
      {testResult.status === InternalApiScenarioTestResultStatus.FAILED && (
        <div className='test-failed-message'>
          <span className='align-middle d-flex'>
            <FontAwesomeIcon icon={faCircleExclamation} className='fe me-2 test-failed-icon' />
            {testResult.statusMessage ||
              'There is either an issue with the scenario, or something happened on our side. Please review the scenario and re-try the test, and if the problem persists contact us.'}
          </span>
        </div>
      )}
    </div>
  );
};
